import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { PERMISSIONS } from '@/constants/permissions';
import { hasAllPermissions } from '@/utils/accountPermissions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { performLogout } from '@/features/auth/store/authSlice';
import { selectEffectivePermissions } from '@/features/auth/store/authSelectors';

/** Same landing URL as {@link AuthGuard} so the login panel opens after session clear. */
const DEFAULT_LOGIN_URL = `${ROUTES.HOME}?login=1`;

/**
 * Fine-grained permission guard (bitmask). Use after {@link AuthGuard}.
 * If the user lacks a required flag, clears the session (logout) and sends them to the login flow.
 *
 * @param {React.ReactNode} children
 * @param {(keyof typeof PERMISSIONS|number)[]} requiredPermissions - Permission keys or numeric flags (all must pass)
 * @param {string} [redirectTo] - Where to navigate after logout (default: home with login panel)
 */
export function PermissionGuard({
    children,
    requiredPermissions = [],
    redirectTo = DEFAULT_LOGIN_URL,
}) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const mask = useAppSelector(selectEffectivePermissions);
    const allowed = hasAllPermissions(mask, ...requiredPermissions);
    const logoutStarted = useRef(false);

    useEffect(() => {
        if (allowed || logoutStarted.current) return;
        logoutStarted.current = true;
        dispatch(performLogout()).finally(() => {
            navigate(redirectTo, { replace: true });
        });
    }, [allowed, dispatch, navigate, redirectTo]);

    if (!allowed) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        );
    }

    return children;
}

export default PermissionGuard;
